import { ConnectApi } from '@js/plugins/connectApi';

/**
 * @description Initiates and completes a flight search, then fetches results.
 * @param searchParams - search parameters object
 * @param sorting - sorting string
 * @param page - page number
 * @param filters - optional filters object
 * @returns Flight search results
 */
export async function executeFlightSearch (
	searchParams,
	sorting,
	page,
	filters = {}
) {
	console.log(`Starting search:`, JSON.stringify({ searchParams, sorting, page, filters }, null, 2));

	// Start search
	const startRes = await startSearch(searchParams).catch((err) => {
		console.error('Search initiation error:', err);
		throw err;
	});

	if (!startRes) {
		throw new Error(`Search API error: No response`);
	}

	const { results: { masterid, server, id } = {} } = startRes || {};
	if (!masterid || !server || !id) {
		throw new Error('Unexpected search response: missing masterid/server/id');
	}

	console.log(`Search started. masterid: ${masterid}, server: ${server}, id: ${id}`);

	// Poll for completion
	await pollSearchCompletion(masterid, server, id).catch((err) => {
		console.error('Search polling error:', err);
		throw err;
	});

	console.log('Search completed, fetching results...');

	// Fetch results
	const resultsRes = await fetchSearchResults(id, sorting, page).catch((err) => {
		console.error('Fetch results error:', err);
		throw err;
	});

	if (!resultsRes) {
		throw new Error(`Fetch results API error: No response`);
	}

	return resultsRes;
}

export function startSearch (params) {
	console.log(`Starting search with params:`, JSON.stringify(params, null, 2));
	return ConnectApi('Search', 'initiateSearch', [params]).catch((err) => {
		console.error('Search initiation error:', err);
		throw err;
	});
}

function checkSearchStatus (responseId) {
	return ConnectApi('Search', 'update', [{ responseId }]).catch((err) => {
		console.error('Search status error:', err);
		throw err;
	});
}

export function fetchSearchResults (id, sorting, page, filters = {}) {
	const params = { id, sorting, page, initial: true, filters };
	console.log(`Fetching search results with params:`, JSON.stringify(params, null, 2));
	return ConnectApi('Search', 'getResult', [params]).catch((err) => {
		console.error('Fetch results error:', err);
		throw err;
	});
}

export function pollSearchCompletion (
	id,
	intervalMs = 1000,
	timeoutMs = 45000,
	updateCallback = null
) {
	console.log(`Polling for completion https://www.flygresor.se/resultat?id=${id}`);

	return new Promise((resolve, reject) => {
		const startTime = Date.now();
		const poll = async () => {
			try {
				const statusRes = await checkSearchStatus(id);
				updateCallback?.(statusRes);
				console.log(`Polled search status:`, statusRes?.results?.percentage, '% complete');

				const state = statusRes?.results?.status;
				if (state === 'complete') {
					resolve();
				} else if (Date.now() - startTime > timeoutMs) {
					reject(new Error(`Search did not complete within ${timeoutMs / 1000}s.`));
				} else if (statusRes.error) {
					reject(new Error(`Search error: ${JSON.stringify(statusRes)}`));
				} else {
					setTimeout(poll, intervalMs);
				}
			} catch (err) {
				reject(err);
			}
		};
		poll();
	});
}
